import React, { useState, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Slide from '@material-ui/core/Slide';
import axios from 'axios';
import { numberAsCurrency } from '../utils';

// Prova social: aviso que desce do topo com ganhadores instantâneos reais.
// Cada ganhador aparece uma única vez por dispositivo (ids no localStorage),
// em intervalos irregulares — quando a fila acaba, o aviso some até chegarem
// ganhadores novos (sem loop, pra não parecer artificial).
const STORAGE_KEY = 'eds_shown_instant_winners';
const MAX_STORED_IDS = 200;
const FIRST_DELAY_MS = 4000;
const SHOW_MS = 6000;
const EXIT_MS = 350;
const MIN_GAP_MS = 8000;
const MAX_GAP_MS = 15000;
const POLL_MS = 60000;

const useStyles = makeStyles((theme) => ({
  wrapper: {
    position: 'fixed',
    top: '70px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 1300,
    width: 'auto',
    maxWidth: 'calc(100vw - 32px)',
    pointerEvents: 'none',
  },
  card: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E3E3E3',
    borderRadius: '14px',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
    padding: '10px 16px',
    whiteSpace: 'nowrap',
  },
  icon: {
    fontSize: '1.4rem',
    lineHeight: 1,
  },
  text: {
    fontSize: '0.9rem',
    color: '#1A1A1A',
    '& strong': {
      fontWeight: 700,
    },
    [theme.breakpoints.down('xs')]: {
      whiteSpace: 'normal',
    },
  },
  value: {
    color: '#2D6E2F',
    fontWeight: 700,
  },
}));

function loadShownIds() {
  if (typeof localStorage === 'undefined') return [];
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return Array.isArray(stored) ? stored : [];
  } catch (error) {
    return [];
  }
}

function markShown(id) {
  if (typeof localStorage === 'undefined') return;
  const ids = loadShownIds();
  ids.push(id);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(ids.slice(-MAX_STORED_IDS)));
}

export default function InstantWinnerToast({ influencer }) {
  const classes = useStyles();
  const [current, setCurrent] = useState(null);
  const [visible, setVisible] = useState(false);
  const queueRef = useRef([]);
  const queuedIdsRef = useRef(new Set());
  const busyRef = useRef(false);
  const timersRef = useRef([]);
  const mountedRef = useRef(true);

  function schedule(fn, ms) {
    const timer = setTimeout(fn, ms);
    timersRef.current.push(timer);
    return timer;
  }

  function showNext(delay) {
    if (busyRef.current || queueRef.current.length === 0) return;
    busyRef.current = true;

    schedule(() => {
      if (!mountedRef.current) return;
      const winner = queueRef.current.shift();
      if (!winner) {
        busyRef.current = false;
        return;
      }
      markShown(winner.id);
      setCurrent(winner);
      setVisible(true);

      schedule(() => {
        if (!mountedRef.current) return;
        setVisible(false);
        schedule(() => {
          busyRef.current = false;
          if (mountedRef.current) {
            showNext(MIN_GAP_MS + Math.random() * (MAX_GAP_MS - MIN_GAP_MS));
          }
        }, EXIT_MS);
      }, SHOW_MS);
    }, delay);
  }

  async function fetchWinners(initial) {
    if (!influencer || !influencer.username) return;
    try {
      const response = await axios.get(`/api/v1/campaigns/${influencer.username}/recent_instant_winners.json`);
      const shown = new Set(loadShownIds());
      const fresh = (response.data || []).filter((winner) => (
        winner.first_name
          && winner.prize_value != null
          && !shown.has(winner.id)
          && !queuedIdsRef.current.has(winner.id)
      ));
      if (fresh.length === 0) return;

      fresh.forEach((winner) => queuedIdsRef.current.add(winner.id));
      queueRef.current.push(...fresh);
      showNext(initial ? FIRST_DELAY_MS : MIN_GAP_MS);
    } catch (error) {
      // silencioso: prova social nunca pode quebrar a Home
    }
  }

  useEffect(() => {
    mountedRef.current = true;
    fetchWinners(true);
    const poll = setInterval(() => fetchWinners(false), POLL_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(poll);
      timersRef.current.forEach(clearTimeout);
    };
  }, []);

  if (!current) return null;

  return (
    <div className={classes.wrapper}>
      <Slide direction="down" in={visible} timeout={EXIT_MS}>
        <div className={classes.card}>
          <span className={classes.icon} role="img" aria-hidden="true">🎁</span>
          <span className={classes.text}>
            <strong>{current.first_name}</strong>
            {' ganhou '}
            <span className={classes.value}>{numberAsCurrency(current.prize_value)}</span>
            {' em prêmio instantâneo'}
          </span>
        </div>
      </Slide>
    </div>
  );
}
